import { useState } from 'react';
import {
    useGetMemberMutation,
    useToggleArchiveMemberMutation,
    useResetPasswordMemberMutation
} from '@services/member/member/member.endpoints';
import { Member, SocialBenefit, FinancialBenefitSchedule, Contribution } from '@services/member/member/member.types';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Booking } from '@services/member/booking/booking.types';

export const useDetailsPreviewVM = () => {
    const MySwal = withReactContent(Swal);

    const { id } = useParams();
    const [member, setMember] = useState<Member>();
    const socialBenefitColumnHelper = createColumnHelper<SocialBenefit>();
    const financialBenefitScheduleColumnHelper = createColumnHelper<FinancialBenefitSchedule>();
    const contributionColumnHelper = createColumnHelper<Contribution>();
    const bookingColumnHelper = createColumnHelper<Booking>();
    const [
        toggleArchiveMember,
        {
            isSuccess: isToggleArchiveMemberSuccess,
            isLoading: isToggleArchiveMemberLoading,
            isError: isToggleArchiveMemberError,
            error: toggleArchiveMemberError,
            data: toggleArchiveMemberResponse
        }
    ] = useToggleArchiveMemberMutation();

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isUninitialized: isGetMemberUninitialized,
            isError: isGetMemberError,
            isSuccess: isGetMemberSuccess,
            error: getMemberError,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();

    useEffect(() => {
        if (!id) return;
        getMember({ id });
    }, [id]);

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError) return;
        if (getMemberError) return;

        if (isGetMemberSuccess && getMemberResponse) {
            const sortedMemberContributions = [...getMemberResponse.data.lastContributions].sort((a: Contribution, b: Contribution) => {
                return new Date(b.dateContributed ?? '').getTime() - new Date(a.dateContributed ?? '').getTime();
            });
            const sortedMemberSocialBenefits = [...getMemberResponse.data.lastSocialBenefits].sort((a: SocialBenefit, b: SocialBenefit) => {
                return new Date(b.assignedAt ?? '').getTime() - new Date(a.assignedAt ?? '').getTime();
            });
            const sortedMemberBookings = [...getMemberResponse.data.lastBookings].sort((a: Booking, b: Booking) => {
                return new Date(b.arrivalDate ?? '').getTime() - new Date(a.arrivalDate ?? '').getTime();
            });
            setMember({
                ...getMemberResponse.data,
                lastContributions: sortedMemberContributions,
                lastSocialBenefits: sortedMemberSocialBenefits,
                lastBookings: sortedMemberBookings
            });
        }
    }, [isGetMemberError, isGetMemberSuccess, getMemberError, getMemberResponse, isGetMemberLoading]);

    useEffect(() => {
        if (isToggleArchiveMemberLoading) return;
        if (isToggleArchiveMemberError) return;
        if (toggleArchiveMemberError) return;

        if (isToggleArchiveMemberSuccess && toggleArchiveMemberResponse) {
            if (!id) return;
            getMember({ id });
        }
    }, [
        isToggleArchiveMemberError,
        isToggleArchiveMemberSuccess,
        toggleArchiveMemberError,
        toggleArchiveMemberResponse,
        isToggleArchiveMemberLoading
    ]);

    const [
        resetPasswordMember,
        {
            isLoading: isResetPasswordLoading,
            isSuccess: isResetPasswordSuccess,
            isError: isResetPasswordError,
            error: resetPasswordError,
            data: resetPasswordResponse
        }
    ] = useResetPasswordMemberMutation();

    useEffect(() => {
        if (isResetPasswordLoading) return;
        if (isResetPasswordError) return;
        if (resetPasswordError) return;

        if (isResetPasswordSuccess && resetPasswordResponse) {
            MySwal.fire({
                title: 'Mot de passe réinitialisé avec succès',
                icon: 'success',
                confirmButtonColor: '#01539D',
                confirmButtonText: 'OK'
            });
        }
    }, [isResetPasswordError, isResetPasswordSuccess, resetPasswordError, resetPasswordResponse, isResetPasswordLoading]);

    const handleToggleArchiveMember = () => {
        if (!member) return;
        MySwal.fire({
            title: member.isArchived
                ? 'Êtes-vous sûr de vouloir désarchiver cet adhérent ?'
                : 'Êtes-vous sûr de vouloir archiver cet adhérent ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: member.isArchived ? 'Désarchiver' : 'Archiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                toggleArchiveMember({ id: member.id, archived: !member.isArchived });
            }
        });
    };

    const handleResetPassword = () => {
        if (!member) return;
        if (member.wordpressId) {
            MySwal.fire({
                title: 'Êtes-vous sûr de vouloir réinitialiser le mot de passe de cet adhérent ?',
                icon: 'warning',
                showCancelButton: true,
                iconColor: '#01539D',
                confirmButtonColor: '#01539D',
                confirmButtonText: 'Réinitialiser',
                cancelButtonText: 'Annuler'
            }).then((result) => {
                if (result.isConfirmed) {
                    resetPasswordMember({ id: String(member.id) });
                }
            });
        } else {
            MySwal.fire({
                title: "Cet utilisateur n'a pas de compte sur le site internet COSEM.",
                icon: 'info',
                showCancelButton: false,
                confirmButtonColor: '#FF5F46',
                confirmButtonText: 'Fermer'
            });
        }
    };

    return {
        isLoading: isGetMemberUninitialized || isGetMemberLoading,
        isError: isGetMemberError && !member,
        member,
        socialBenefitColumnHelper,
        financialBenefitScheduleColumnHelper,
        contributionColumnHelper,
        bookingColumnHelper,
        handleToggleArchiveMember,
        handleResetPassword
    };
};
